<script setup lang="ts">
import type { FormKitNode } from '@formkit/core'

interface Voucher {
  hash: string,
  product: string,
  supplier: any,
  name: string,
  options: any,
  expireDate: string,
}

interface Props {
  voucher: Voucher,
  manufacturers: Array<any>,
  contractors: Array<any>
}

interface Form {
  manufacturer: string,
  email: string,
  phoneNumber: string,
}

const { voucher, manufacturers, contractors } = defineProps<Props>()

const { executeRecaptcha } = useGoogleRecaptcha()
const store = useStore()

const submitSucceeded = ref<boolean>(false)
const reservation = ref<string | null>(null)

const manufacturerOptions = computed(() => manufacturers.map(({ name, pimcoreId }) => ({
  label: name,
  value: pimcoreId
})))

const showManufacturers = computed(() => !voucher.supplier && manufacturerOptions.value.length > 1 || getContractors(manufacturers[0].name))

const getName = (name, options) => {
  return [name, ...options.map(({ group, option }) => `${group}: ${option}`)].join(', ')
}

const getContractors = (name: string) => {
  return contractors.find(contractor => contractor.name === name)
}

const submit = async (form: Form, node: FormKitNode) => {
  node.clearErrors()
  const token = await executeRecaptcha('reservation')
  try {
    reservation.value = await $fetch('/store-api/reservation', {
      method: 'POST',
      body: {
        form: {
          ...form,
          manufacturer: voucher.supplier ? voucher.supplier.supplierIdMap.pimId.toString() : form.manufacturer,
          hash: voucher.hash
        },
        token
      }
    })
    submitSucceeded.value = true
  } catch (e) {
    node.setErrors(
      JSON.stringify(e)
    )
  }
}
</script>

<template>
  <div v-if="submitSucceeded">
    <div v-if="reservation">
      <Alert>
        Dane kontaktowe zostały wysłane poprawnie na Twojego maila - sprawdź swoją skrzynkę pocztową
      </Alert>
      <div class="text-16 md:text-18 font-bold my-[30px]">
        Twój prezent realizuje {{ reservation.supplierName }}
      </div>
      <div class="text-16 md:text-18 font-bold my-[30px]">
        Kontakt do rezerwacji Twojego prezentu - zadzwoń/napisz i umów termin
      </div>
      <div v-for="contactGroup in reservation.reservationContactGroups" :key="contactGroup.id">
        <div class="text-12 md:text-14">
          {{ contactGroup.groupName }}
        </div>
        <div class="text-12 md:text-14 mb-[15px]">
          <div v-html="formatHtml(contactGroup.groupDescription)" />
        </div>
        <div v-for="contact in contactGroup.reservationContacts" :key="contact.id" class="text-12 md:text-14 mb-[15px]">
          {{ contact.contactName }}, {{ contact.contactPhone }}, {{ contact.contactEmail }}
        </div>
      </div>
      <div class="text-12 md:text-14 font-bold">
        Adres:
      </div>
      <div class="text-12 md:text-14 mb-[15px]">
        {{ reservation.realizationPlace }}
      </div>
      <div class="text-12 md:text-14 font-bold mb-[30px]">
        Adres realizacji atrakcji może być inny niż powyższy lub ten na voucherze, potwierdź miejsce spotkania z instruktorem.
      </div>
      <div class="text-12 md:text-14 font-bold">
        Twój voucher to:
      </div>
      <div class="text-12 md:text-14 mb-[15px]">
        {{ getName(reservation.voucher.name, reservation.voucher.options) }}
      </div>
      <div class="text-12 md:text-14 font-bold">
        Nr rezerwacyjny:
      </div>
      <div class="text-12 md:text-14 mb-[15px]">
        {{ reservation.voucher.hash }}
      </div>
      <div class="text-12 md:text-14 font-bold">
        Data ważności vouchera:
      </div>
      <div class="text-12 md:text-14">
        {{ useLocaleDate(new Date(reservation.voucher.expireDate)) }}
      </div>
    </div>
    <div v-else>
      <Alert type="error" class="mb-[20px]">
        Błąd.
      </Alert>
      <NuxtLink to="/kontakt" class="block underline">
        Skontaktuj się z nami
      </NuxtLink>
    </div>
  </div>
  <div v-else>
    <div class="md:w-2/5">
      <FormKit
        type="form"
        id="registration-form"
        :actions="false" 
        :incomplete-message="false"
        @submit="submit"
      >
        <FormKit
          v-show="showManufacturers"
          type="radio"
          name="manufacturer"
          label="Dostępni wykonawcy i planowane terminy:"
          legend-class="text-18 md:text-20 font-bold mb-[15px]"
          validation="required"
          :validation-messages="{
            required: 'Wybierz wykonawcę atrakcji.'
          }"
          :options="manufacturerOptions"
          :value="voucher.supplier || manufacturers.length === 1 ? manufacturers[0].pimcoreId : ''"
          
        >
        <template #label="context">
          <span class="cursor-pointer">{{ context.option.label }}</span>
          <NuxtLink v-if="getContractors(context.option.label)" :to="{ name: 'kalendarz', params: { contractor: context.option.label } }" class="flex items-center gap-[5px] text-12 text-primary" target="_blank">
            <span>Sprawdź terminy jazd</span>
            <i class="icon-arrow-button text-10 text-primary" />
          </NuxtLink>
        </template>
        </FormKit>
        <div v-if="voucher.supplier">
          <div class="text-16 md:text-18 font-bold mb-[15px]">
            Wysłaliśmy Ci już maila z danymi instruktora.
          </div>
          <div class="text-12 md:text-14 mb-[15px]">
            Wysłaliśmy Ci już maila z danymi instruktora bądź firmy, gdzie rezerwuje i realizuje się voucher. Aby otrzymać je ponownie podaj swoje dane kontaktowe.
          </div>
        </div>
        <div v-else>
          <div class="text-16 md:text-18 font-bold mb-[15px]">
            Aby zrealizować ten voucher, podaj swoje dane
          </div>
          <div class="text-12 md:text-14 mb-[15px]">
            Wyślemy Ci wiadomość na adres mailowy z danymi firmy gdzie bezpośrednio dokonasz rezerwacji i zrealizujesz voucher
          </div>
        </div>
        <FormKit
          type="text"
          outer-class="mb-[10px]"
          name="email"
          label="E-mail*"
          placeholder="E-mail*"
          validation="required|email"
          validation-visibility="blur"
          :validation-messages="{
            required: 'Pole e-mail nie może być puste.',
            email: 'Błędny adres e-mail.'
          }"
        />
        <FormKit
          type="tel"
          outer-class="mb-[10px]"
          name="phoneNumber"
          label="Numer telefonu*"
          placeholder="Numer telefonu*"
          validation="required|phone"
          validation-visibility="blur"
          :validation-messages="{
            required: 'Pole numer telefonu nie może być puste.',
            phone: 'Numer telefonu jest błędny.'
          }"
        />
        <FormKit
          type="checkbox"
          outer-class="mb-[10px]"
          name="rules"
          validation="accepted"
          validation-visibility="blur"
          :validation-messages="{
            accepted: 'Zaakceptuj regulamin.'
          }"
        >
          <template #suffix>
            <div>
              Oświadczam, iż zapoznałem/am się z treścią <NuxtLink to="/i/regulamin" target="_blank" class="underline">Regulaminu</NuxtLink> i akceptuję jego postanowienia w celu realizacji vouchera.*
            </div>
          </template>
        </FormKit>
        <FormKit
          type="checkbox"
          outer-class="mb-[10px]"
          name="rules2"
          validation="accepted"
          validation-visibility="blur"
          :validation-messages="{
            accepted: 'Zaakceptuj zgodę.'
          }"
        >
          <template #suffix>
            <div>
              Wyrażam zgodę na przetwarzanie moich danych osobowych podanych w formularzu przez EMOTIVO sp. z o. o. w Puławach (24-100), ul. 6 Sierpnia 1, w sposób opisany w
              <NuxtLink to="/i/polityka-prywatnosci" target="_blank" class="underline">polityce prywatności</NuxtLink>
              w celu uzyskania odpowiedzi na pytanie, skontaktowania się na wskazane dane do korespondencji - zgodnie ze złożonym zapytaniem. Zgoda jest dobrowolna i może być w każdej chwili wycofana, kierując wiadomość na adres e-mail administratora. Wycofanie zgody nie wpływa na zgodność z prawem przetwarzania, którego dokonano na podstawie zgody przed jej wycofaniem.*
            </div>
          </template>
        </FormKit>
        <FormKit
          type="submit"
          input-class="btn btn-action mt-[15px]"
        >
          <span class="text-16">Wyślij</span>
          <i class="icon-arrow-button text-[10px]" />
        </FormKit>
      </FormKit>
      <div class="bg-red text-12 text-white mt-[30px] p-[20px]">
        Rezerwacja vouchera musi być dokonana z minimum 7 dniowym wyprzedzeniem, nie później niż 30 dni przed końcem ważności vouchera lub końcem sezonu (w przypadku prezentów sezonowych). Przed rezerwacją zapoznaj się z Regulaminem.
      </div>
    </div>
  </div>
</template>
